import { type FormEvent, useState } from 'react'
import toast from 'react-hot-toast'

import { ApiCaller } from '@frontend/api'
import { captureException } from '@sentry/react'
import type { Rating } from '@types'
import {
	Button, Modal, StarRating, Textarea
} from '@ui/components'
interface FeedbackModalProps {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly selectedRatingNote: number;
	readonly setSelectedRatingNote: (rating: number) => void;
	readonly type: 'message' | 'blockMessage';
	readonly id: string;
	readonly setRating: (rating: Rating) => void;
}

export default function FeedbackModal({ isOpen, selectedRatingNote, setSelectedRatingNote, type, id, onClose, setRating }: FeedbackModalProps) {
	const [feedback, setFeedback] = useState('')
	const [confidentialFeedback, setConfidentialFeedback] = useState(true)

	async function handleSubmit(e?: FormEvent<HTMLFormElement> | undefined) {
		if (e) {
			e.preventDefault()
		}

		if (type === 'message') {
			try {
				const rating = await ApiCaller.createRating({
					rating: selectedRatingNote.toString() as '1' | '2' | '3' | '4' | '5',
					feedback,
					messageId: id,
					confidentialFeedback
				})

				setRating(rating)
				ApiCaller.track('Feedback_Sent')
				toast.success('Feedback sent successfully')
			} catch (error) {
				captureException(error)
			}
		}

		onClose()
	}

	return (
		<Modal
			isOpen={ isOpen }
			title="Our team will review your feedback"
			size="medium"
			onClose={ onClose }
		>
			<div className="-mt-6 mb-4">
				<StarRating
					selectedRating={ selectedRatingNote }
					onRating={ setSelectedRatingNote }
				/>
			</div>
			<form onSubmit={ handleSubmit }>
				<p className="text-sm text-gray-500 mb-2">What could be improved? (Optional)</p>
				<Textarea
					label=""
					value={ feedback }
					placeholder="Tell us more about your experience with this answer..."
					customClass="min-h-[100px] mb-2 shadow-sm"
					onChange={ e => {
						setFeedback(e)
					} }
				/>

				<div className="mb-3 mt-1">
					<div className="flex items-start gap-2 mb-1 hover:bg-gray-50 rounded-md p-2">
						<input
							defaultChecked={ confidentialFeedback }
							type="radio"
							id="confidential"
							name="shareOption"
							value="confidential"
							className="mt-1 cursor-pointer"
							onChange={ () => {
								setConfidentialFeedback(true)
							} }
						/>
						<label htmlFor="confidential" className="text-sm text-gray-600 cursor-pointer">
						⛔ This is confidential data, I don&apos;t want the team to review my answer directly.
						</label>
					</div>
					<div className="flex items-start gap-2 hover:bg-gray-50 rounded-md p-2">
						<input
							type="radio"
							id="share"
							name="shareOption"
							value="share"
							className="mt-1 cursor-pointer"
							onChange={ () => {
								setConfidentialFeedback(false)
							} }
						/>
						<label htmlFor="share" className="text-sm text-gray-600 cursor-pointer">
						🔓 I agree to securely share this answer with the VirtualBrain Team to help improve the product.
						</label>
					</div>
				</div>
				<div className="flex justify-end">
					<div className="w-fit">
						<Button
							type="submit"
							text="Send my feedback"
						/>
					</div>
				</div>
			</form>
		</Modal>
	)
}
